import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Modal } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, FileTextOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { authService } from '../../../services/auth';
import type { FactoryOptionDto, SupplierOptionDto } from '../../../dto/reference';
import { fetchFactoryOptions, fetchSupplierOptions } from '../../reference-data/api/referenceDataApi';
import {
  createSupplierOrder,
  fetchSuggestedSupplierOrderNumber,
  getSupplierOrderDisplayNumber,
} from '../api/supplierOrdersApi';
import { getSupplierOrderErrorMessage, isBlockingSupplierOrderError } from '../supplierOrderErrorMessages';
import { usePageHeaderTitleOverride } from '../../navigation/pageHeaderTitleOverride';
import {
  PAGE_SHELL_STYLE,
} from '../orderDetailLayout';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import '../process/OrderProcess.css';
import { useFeedback } from '../../../utils/feedback';
import { SupplierOrderItemsSection } from '../sections/SupplierOrderItemsSection';
import SupplierOrderChooseSupplierModal from '../sections/SupplierOrderChooseSupplierModal';
import SupplierOrderDraftStagePanel from './SupplierOrderDraftStagePanel';
import SupplierOrderMoreDataSection from './SupplierOrderMoreDataSection';
import {
  buildNewSupplierOrderPayload,
  buildUnsavedSupplierOrderView,
  type UnsavedSupplierOrderDraft,
} from './supplierOrderNewDraft';

// Rendered by pages/SupplierOrderDetail.tsx, which loads the shared detail
// theme (OrderDetail.css) for both the new and the saved ZD card.
export const SUPPLIER_ORDERS_LIST_PATH = '/app/orders?orderFlow=supplier';

/**
 * `/app/supplier-orders/new` (karta ZD według makiety, 2026-10-05): a new ZD
 * is the ZD card in stage „Szkic”, not a separate wizard. Nothing is saved
 * until the supplier is chosen — then the ordinary POST /supplier-orders
 * creates the ZD with status „Szkic”, the lines and „Więcej danych”, and the
 * page moves to the saved card `/app/supplier-orders/:id`.
 */
const SupplierOrderNewDraftCard: React.FC = () => {
  const { t } = useTranslation(['orders', 'common']);
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const canManage = authService.canManageSupplierOrders();
  const canEditPurchasePrices = authService.canEditSupplierOrderPurchasePrices();
  const canViewPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  usePageHeaderTitleOverride(t('orders:supplierOrders.newSupplierOrder'));

  const [draft, setDraft] = useState<UnsavedSupplierOrderDraft>(() => ({
    currency: 'PLN',
    items: [],
    more: { orderDate: dayjs(), useManualNumbering: false },
  }));
  const [moreTouched, setMoreTouched] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [factories, setFactories] = useState<FactoryOptionDto[]>([]);
  const [autoNumberPreview, setAutoNumberPreview] = useState<string | null>(null);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [linesOpen, setLinesOpen] = useState(false);
  const view = useMemo(() => buildUnsavedSupplierOrderView(draft), [draft]);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        const [supplierRows, factoryRows] = await Promise.all([fetchSupplierOptions(), fetchFactoryOptions()]);
        setSuppliers(supplierRows);
        setFactories(factoryRows);
      } catch {
        // Non-critical — the selects stay empty and say so.
      }
    })();
  }, [canManage]);

  const orderDateIso = draft.more.orderDate && dayjs.isDayjs(draft.more.orderDate) ? draft.more.orderDate.toISOString() : undefined;
  useEffect(() => {
    if (!canManage) return undefined;
    let ignore = false;
    (async () => {
      try {
        const suggestion = await fetchSuggestedSupplierOrderNumber(orderDateIso);
        if (!ignore) setAutoNumberPreview(suggestion);
      } catch {
        // Preview only — the binding number is given at save.
      }
    })();
    return () => { ignore = true; };
  }, [canManage, orderDateIso]);

  const dirty = draft.items.length > 0 || moreTouched;
  // Closing or reloading the tab loses the unsaved card; the browser asks first
  // (same pattern as the new customer order, pages/OrderNew.tsx). The side menu
  // does not ask — same as there.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const backToList = () => {
    if (!dirty) {
      goBack(SUPPLIER_ORDERS_LIST_PATH);
      return;
    }
    modal.confirm({
      title: t('orders:supplierOrders.card.leaveNewTitle'),
      content: t('orders:supplierOrders.card.leaveNewContent'),
      okText: t('orders:supplierOrders.card.leaveNewOk'),
      cancelText: t('common:cancel'),
      onOk: () => goBack(SUPPLIER_ORDERS_LIST_PATH),
    });
  };

  // „Wybierz dostawcę” / „Zapisz szkic” — the first save of the new ZD.
  const createWithSupplier = async (supplierId: string, currency: string | null): Promise<boolean> => {
    try {
      const created = await createSupplierOrder(
        buildNewSupplierOrderPayload({ draft, supplierId, currency, canEditPurchasePrices }),
        { modal, t },
      );
      if (!created) return false;
      feedback.success(t('orders:supplierOrders.card.createdDraft', { orderNumber: getSupplierOrderDisplayNumber(created) }));
      navigate(`/app/supplier-orders/${created.id}`, { replace: true });
      // The saved card opens at its top (stage panel), not where „Więcej danych” was.
      if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0 });
      return true;
    } catch (error) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.messages.createError'), {
        sticky: isBlockingSupplierOrderError(error),
      });
      return false;
    }
  };

  const meta = [
    { icon: <ShopOutlined />, label: t('orders:supplierOrders.form.supplier'), value: t('orders:supplierOrders.card.supplierPending') },
    { icon: <FileTextOutlined />, label: t('orders:supplierOrders.card.originLabel'), value: t('orders:supplierOrders.card.originNew') },
    {
      icon: <CalendarOutlined />,
      label: t('orders:supplierOrders.form.orderDate'),
      value: orderDateIso ? formatDateEuropean(orderDateIso) : '-',
    },
  ];

  return (
    <div className="zk-page">
      <div style={PAGE_SHELL_STYLE} className="order-detail-page zk-process" data-testid="supplier-order-new-card">
        {/* Same header as the saved ZD card (pages/SupplierOrderDetail.tsx, process/DESIGN.md). */}
        <section className="zk-section zk-head" data-testid="supplier-order-header">
          <div className="zk-head__main">
            <div className="zk-head__tags">
              <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
                {t('orders:process.back')}
              </Button>
              <span className="zk-tag">{t('orders:supplierOrders.newSupplierOrder')}</span>
              <span className="zk-pill">{t('orders:supplierOrders.status.DRAFT')}</span>
              <span style={{ color: 'var(--app-text-secondary)', fontSize: 12 }}>{t('orders:supplierOrders.card.notSavedYet')}</span>
            </div>
          </div>
          <div className="zk-chips">
            {meta.map((item) => (
              <div className="zk-chip" key={item.label}>
                <span className="zk-chip__label">{item.label}</span>
                <span className="zk-chip__value">
                  {item.icon}
                  <span className="zk-chip__text" title={typeof item.value === 'string' ? item.value : undefined}>{item.value}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        {!canManage ? (
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('orders:supplierOrders.card.noPermission')} />
        ) : null}

        <SupplierOrderDraftStagePanel
          isNew
          supplierName={null}
          lines={draft.items}
          pricesVisible={canViewPurchasePrices}
          canManage={canManage}
          canReject={false}
          canSendStatus={false}
          onChooseSupplier={() => setChooseOpen(true)}
          onOpenLines={() => setLinesOpen(true)}
          onSend={() => setChooseOpen(true)}
          onReject={() => undefined}
        >
          <SupplierOrderMoreDataSection
            values={draft.more}
            factories={factories}
            canEdit={canManage}
            autoNumberPreview={autoNumberPreview}
            onFactoryCreated={(factory) => setFactories((current) => [...current, factory])}
            onChange={(more) => {
              setMoreTouched(true);
              setDraft((current) => ({ ...current, more }));
            }}
          />
        </SupplierOrderDraftStagePanel>

        <SupplierOrderItemsSection
          supplierOrder={view}
          canEdit={false}
          canViewSupplierPurchasePrices={canViewPurchasePrices}
          onSaved={async () => undefined}
        />
      </div>

      {canManage ? (
        <>
          <Modal
            open={linesOpen}
            title={t('orders:supplierOrders.card.linesTitle', { number: t('orders:supplierOrders.card.newNumber') })}
            footer={null}
            width={920}
            onCancel={() => setLinesOpen(false)}
            destroyOnHidden
            maskClosable={false}
          >
            <SupplierOrderItemsSection
              layout="dialog"
              supplierOrder={view}
              canEdit
              canViewSupplierPurchasePrices={canViewPurchasePrices}
              saveItems={async (items) => {
                setDraft((current) => ({ ...current, items }));
                feedback.success(t('orders:supplierOrders.card.linesKept'));
                return true;
              }}
              onSaved={async () => setLinesOpen(false)}
              onCancel={() => setLinesOpen(false)}
            />
          </Modal>
          <SupplierOrderChooseSupplierModal
            open={chooseOpen}
            supplierOrder={view}
            suppliers={suppliers}
            canEditPurchasePrices={canEditPurchasePrices}
            onClose={() => setChooseOpen(false)}
            onSaved={() => undefined}
            onSubmitChoice={createWithSupplier}
            onSupplierCreated={(supplier) => setSuppliers((current) => [...current, supplier])}
          />
        </>
      ) : null}
    </div>
  );
};

export default SupplierOrderNewDraftCard;
